import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SEO } from '@/components/SEO';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { translateServerError } from '@/lib/serverErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactPage() {
  const { t } = useTranslation();
  const [form, setForm] = React.useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const update = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t('contact.validationRequired');
    if (!form.email.trim()) e.email = t('contact.validationRequired');
    else if (!EMAIL_RE.test(form.email.trim())) e.email = t('contact.validationInvalidEmail');
    if (!form.subject.trim()) e.subject = t('contact.validationRequired');
    if (!form.message.trim()) e.message = t('contact.validationRequired');
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      toast.success(t('contact.successToast'));
      setSent(true);
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      const detail = translateServerError(err?.response?.data?.detail) || t('contact.errorToast');
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        title={t('seo.contact.title')}
        description={t('seo.contact.description')}
        canonical="https://ridalo.co/contact"
      />
      <Header />
      <main className="flex-1 py-14 sm:py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="contact-page">
          <h1 className="text-4xl sm:text-5xl font-bold text-primary tracking-tight">
            {t('contact.pageTitle')}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
            {t('contact.pageIntro')}
          </p>

          {sent && (
            <div
              className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700"
              data-testid="contact-success"
              role="status"
            >
              {t('contact.successBanner')}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="contact-form" noValidate>
            <div>
              <Label htmlFor="contact-name">{t('contact.fieldName')}</Label>
              <Input
                id="contact-name"
                type="text"
                value={form.name}
                onChange={update('name')}
                autoComplete="name"
                data-testid="contact-input-name"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'contact-name-error' : undefined}
                className="mt-1"
              />
              {errors.name && (
                <p id="contact-name-error" className="mt-1 text-sm text-red-600" data-testid="contact-error-name">
                  {errors.name}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="contact-email">{t('contact.fieldEmail')}</Label>
              <Input
                id="contact-email"
                type="email"
                value={form.email}
                onChange={update('email')}
                autoComplete="email"
                data-testid="contact-input-email"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'contact-email-error' : undefined}
                className="mt-1"
              />
              {errors.email && (
                <p id="contact-email-error" className="mt-1 text-sm text-red-600" data-testid="contact-error-email">
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="contact-subject">{t('contact.fieldSubject')}</Label>
              <Input
                id="contact-subject"
                type="text"
                value={form.subject}
                onChange={update('subject')}
                data-testid="contact-input-subject"
                aria-invalid={!!errors.subject}
                aria-describedby={errors.subject ? 'contact-subject-error' : undefined}
                className="mt-1"
              />
              {errors.subject && (
                <p id="contact-subject-error" className="mt-1 text-sm text-red-600" data-testid="contact-error-subject">
                  {errors.subject}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="contact-message">{t('contact.fieldMessage')}</Label>
              <Textarea
                id="contact-message"
                rows={6}
                value={form.message}
                onChange={update('message')}
                data-testid="contact-input-message"
                aria-invalid={!!errors.message}
                aria-describedby={errors.message ? 'contact-message-error' : undefined}
                className="mt-1"
              />
              {errors.message && (
                <p id="contact-message-error" className="mt-1 text-sm text-red-600" data-testid="contact-error-message">
                  {errors.message}
                </p>
              )}
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                className="btn-primary w-full sm:w-auto"
                disabled={submitting}
                data-testid="contact-submit-button"
              >
                {submitting ? t('contact.submittingLabel') : t('contact.submitButton')}
              </Button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
